import { TrekkerEvent, TrekkerEventHandler } from 'react-trekker';
import { ifMatch } from 'react-trekker/dist/subscribers/ifmatch';
import { EventContext, EventType } from 'types/Analytics';

import {
  ActionPointModalButtonClicked,
  BookButtonClicked,
  BookingConfirmed,
  BusinessEventSource,
  CheckoutPageViewed,
  Click,
  ContactHostFormSent,
  ContactHostFormSentClick,
  ContactHostFormViewed,
  ContactOtherHostsClick,
  CookieConsentUpdate,
  DateSelected,
  ExperienceCardClicked,
  ExperienceCardViewed,
  ExperiencePageViewed,
  ExperienceReviewUpvoteChange,
  FAQViewed,
  HostSelected,
  LandingPageAboutWithlocalsOpen,
  LocalPickerSubmitted,
  NumberOfGuestsSelected,
  PageView,
  PaymentFormType,
  PaymentOptionSelected,
  ReadMoreClicked,
  SearchPageViewed,
  TimeSelected,
  TripPaid,
} from './businessLayer';
import { fireOnce } from './tools/helpers';

function addDefaultParameters<Type>(
  businessEvent: Type,
  evt: TrekkerEvent<EventContext, EventType>,
): Type {
  const isInternal = evt.context.account?.email?.endsWith('@withlocals.com');

  return {
    ...businessEvent,
    traffic_type: isInternal ? 'internal' : 'external',
    account_is_host: evt.context.account?.is_host,
    account_id: evt.context.account?.id,
    anonymous_device_id: evt.context.anonymousDeviceId,
    experiments: evt.context.experiments,
  };
}

export const trekkerAdapter = (
  bus: BusinessEventSource,
): TrekkerEventHandler<EventContext, EventType> => {
  return (evt: TrekkerEvent<EventContext, EventType>) => {
    ifMatch(evt, { name: 'page' }, () => {
      const pageView = new PageView();
      const pageViewData = {
        title: evt.context.pageTitle || window.document.title,
        name: evt.context.pageName,
        location: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
      };
      Object.assign(pageView, pageViewData);
      bus.emit(addDefaultParameters(pageView, evt));
    });

    ifMatch(evt, { name: 'view', context: { component: 'faq' } }, () => {
      const faqViewed = new FAQViewed();
      faqViewed.experience = evt.context.experience;
      bus.emit(addDefaultParameters(faqViewed, evt));
    });

    ifMatch(
      evt,
      { name: 'page', context: { trip: true, section: 'checkout' } },
      () => {
        const checkoutPageViewed = new CheckoutPageViewed();
        checkoutPageViewed.trip = evt.context.trip;
        bus.emit(addDefaultParameters(checkoutPageViewed, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'view',
        context: { experience: true, pageName: 'product-page' },
      },
      () => {
        const experiencePageViewed = new ExperiencePageViewed();
        experiencePageViewed.experience = evt.context.experience;
        bus.emit(addDefaultParameters(experiencePageViewed, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'view',
        context: { pageName: 'searchPage' },
      },
      () => {
        const searchPageViewed = new SearchPageViewed();
        searchPageViewed.city = evt.context.city;
        searchPageViewed.category = evt.context.category;
        bus.emit(addDefaultParameters(searchPageViewed, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'view',
        context: { pageName: 'contactHostForm' },
      },
      () => {
        const contactHostFormViewed = new ContactHostFormViewed();
        contactHostFormViewed.host = evt.context.host;
        contactHostFormViewed.experience = evt.context.experience;
        contactHostFormViewed.enabledForm = evt.context.enabledForm;
        contactHostFormViewed.source = evt.context.source;
        bus.emit(addDefaultParameters(contactHostFormViewed, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'submit',
        context: { pageName: 'contactHostFormSent' },
      },
      () => {
        const contactHostFormSent = new ContactHostFormSent();
        contactHostFormSent.host = evt.context.host;
        contactHostFormSent.experience = evt.context.experience;
        contactHostFormSent.enabledForm = evt.context.enabledForm;
        contactHostFormSent.source = evt.context.source;
        contactHostFormSent.nrOfAdults = evt.context.nrOfAdults;
        contactHostFormSent.nrOfChildren = evt.context.nrOfChildren;
        contactHostFormSent.date = evt.context.date;
        contactHostFormSent.time = evt.context.time;
        contactHostFormSent.duration = evt.context.duration;
        bus.emit(addDefaultParameters(contactHostFormSent, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { pageName: 'contactHostFormSent' },
      },
      () => {
        const contactHostFormSentClick = new ContactHostFormSentClick();
        if (evt.context?.buttonText) {
          contactHostFormSentClick.buttonText = evt.context?.buttonText;
          contactHostFormSentClick.host = evt.context.host;
          contactHostFormSentClick.experience = evt.context.experience;
          contactHostFormSentClick.enabledForm = evt.context.enabledForm;
          contactHostFormSentClick.source = evt.context.source;
          contactHostFormSentClick.nrOfAdults = evt.context.nrOfAdults;
          contactHostFormSentClick.nrOfChildren = evt.context.nrOfChildren;
          contactHostFormSentClick.date = evt.context.date;
          contactHostFormSentClick.time = evt.context.time;
          bus.emit(addDefaultParameters(contactHostFormSentClick, evt));
        }
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'SendRequestToOtherHostsButton' },
      },
      () => {
        const contactOtherHostsClick = new ContactOtherHostsClick();
        if (evt.context?.buttonText) {
          contactOtherHostsClick.buttonText = evt.context?.buttonText;
          contactOtherHostsClick.host = evt.context.host;
          contactOtherHostsClick.experience = evt.context.experience;
          contactOtherHostsClick.enabledForm = evt.context.enabledForm;
          contactOtherHostsClick.source = evt.context.source;
          contactOtherHostsClick.nrOfAdults = evt.context.nrOfAdults;
          contactOtherHostsClick.nrOfChildren = evt.context.nrOfChildren;
          contactOtherHostsClick.date = evt.context.date;
          contactOtherHostsClick.time = evt.context.time;
          bus.emit(addDefaultParameters(contactOtherHostsClick, evt));
        }
      },
    );

    ifMatch(
      evt,
      {
        name: 'submit',
        context: { pageName: 'confirmation-page' },
      },
      () => {
        const tripPaid = new TripPaid();
        tripPaid.trip = evt.context.trip;
        bus.emit(addDefaultParameters(tripPaid, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { cookieConsent: true, component: 'cookie-consent' },
      },
      () => {
        const cookieConsentUpdate = new CookieConsentUpdate();
        Object.assign(cookieConsentUpdate, evt.context.cookieConsent);
        bus.emit(addDefaultParameters(cookieConsentUpdate, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'ActionPointModal' },
      },
      () => {
        const actionPointModalButtonClicked =
          new ActionPointModalButtonClicked();
        actionPointModalButtonClicked.pathname = evt.context.path;
        bus.emit(addDefaultParameters(actionPointModalButtonClicked, evt));
      },
    );

    ifMatch(
      evt,
      { name: 'click', context: { component: 'quick-response' } },
      () => {
        const click = new Click();
        click.target = `Quick response: ${evt.context.selectedValue}`;
        bus.emit(addDefaultParameters(click, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'LandingPageAboutWithlocals' },
      },
      () => {
        const landingPageAboutWithlocalsOpen =
          new LandingPageAboutWithlocalsOpen();
        landingPageAboutWithlocalsOpen.buttonText = evt.context.buttonText;
        bus.emit(addDefaultParameters(landingPageAboutWithlocalsOpen, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
        context: { experienceCard: true },
      },
      () => {
        const experienceCardClicked = new ExperienceCardClicked();
        experienceCardClicked.experience = evt.context.experienceCard;
        experienceCardClicked.position = evt.context.position;
        experienceCardClicked.analyticsListName = evt.context.analyticsListName;
        bus.emit(addDefaultParameters(experienceCardClicked, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'view',
        context: { experienceCard: true },
      },
      () => {
        const experienceCardViewed = new ExperienceCardViewed();
        experienceCardViewed.experience = evt.context.experienceCard;
        experienceCardViewed.position = evt.context.position;
        experienceCardViewed.analyticsListName = evt.context.analyticsListName;
        bus.emit(addDefaultParameters(experienceCardViewed, evt));
      },
    );

    // checkout related things below
    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'local-carousel-card' },
      },
      () => {
        const hostSelected = new HostSelected();
        hostSelected.experience = evt.context.experience;
        hostSelected.nrOfAdults = evt.context.nrOfAdults;
        hostSelected.nrOfChildren = evt.context.nrOfChildren;
        hostSelected.host = evt.context.host;
        bus.emit(addDefaultParameters(hostSelected, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'click',
        context: { host: true, component: 'HostCarouselContainer' },
      },
      () => {
        const hostSelected = new HostSelected();
        hostSelected.experience = evt.context.experience;
        hostSelected.nrOfAdults = evt.context.nrOfAdults;
        hostSelected.nrOfChildren = evt.context.nrOfChildren;
        hostSelected.host = evt.context.host;
        bus.emit(addDefaultParameters(hostSelected, evt));
      },
    );
    ifMatch(
      evt,
      { name: 'click', context: { component: 'book-button' } },
      () => {
        const bookButtonClicked = new BookButtonClicked();
        bookButtonClicked.experience = evt.context.experience;
        bookButtonClicked.nrOfAdults = evt.context.nrOfAdults;
        bookButtonClicked.nrOfChildren = evt.context.nrOfChildren;
        bookButtonClicked.host = evt.context.host;
        bookButtonClicked.date = evt.context.date;
        bookButtonClicked.time = evt.context.time;
        bookButtonClicked.buttonText = evt.context?.buttonText;
        bus.emit(addDefaultParameters(bookButtonClicked, evt));
      },
    );
    ifMatch(evt, { name: 'click', context: { component: 'read-more' } }, () => {
      const readMoreClicked = new ReadMoreClicked();
      readMoreClicked.experience = evt.context.experience;
      bus.emit(addDefaultParameters(readMoreClicked, evt));
    });
    ifMatch(
      evt,
      { name: 'click', context: { component: 'day-selector' } },
      () => {
        const dateSelected = new DateSelected();
        dateSelected.experience = evt.context.experience;
        dateSelected.nrOfAdults = evt.context.nrOfAdults;
        dateSelected.nrOfChildren = evt.context.nrOfChildren;
        dateSelected.host = evt.context.host;
        dateSelected.date = evt.context.date;
        bus.emit(addDefaultParameters(dateSelected, evt));
      },
    );
    ifMatch(
      evt,
      { name: 'click', context: { component: 'time-selector' } },
      () => {
        const timeSelected = new TimeSelected();
        timeSelected.experience = evt.context.experience;
        timeSelected.nrOfAdults = evt.context.nrOfAdults;
        timeSelected.nrOfChildren = evt.context.nrOfChildren;
        timeSelected.host = evt.context.host;
        timeSelected.date = evt.context?.date;
        timeSelected.time = evt.context.time;
        bus.emit(addDefaultParameters(timeSelected, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'InputNumberOfPeopleSelectorContactHost' },
      },
      () => {
        const click = new Click();
        click.target = evt.context.component?.join('-');
        bus.emit(addDefaultParameters(click, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'InputNumberOfPeopleSelector' },
      },
      () => {
        const numberOfGuestsSelected = new NumberOfGuestsSelected();
        numberOfGuestsSelected.experience = evt.context.experience;
        numberOfGuestsSelected.nrOfAdults = evt.context.nrOfAdults;
        numberOfGuestsSelected.nrOfChildren = evt.context.nrOfChildren;
        numberOfGuestsSelected.host = evt.context.host;
        bus.emit(addDefaultParameters(numberOfGuestsSelected, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'pick-your-local-button' },
      },
      () => {
        const bookButtonClicked = new BookButtonClicked();
        bookButtonClicked.experience = evt.context.experience;
        bookButtonClicked.nrOfAdults = evt.context.nrOfAdults;
        bookButtonClicked.nrOfChildren = evt.context.nrOfChildren;
        bookButtonClicked.host = evt.context.host;
        bookButtonClicked.buttonText = evt.context?.buttonText;
        bus.emit(addDefaultParameters(bookButtonClicked, evt));
      },
    );
    ifMatch(evt, { context: { component: 'payment-option' } }, () => {
      const paymentOptionSelected = new PaymentOptionSelected();
      paymentOptionSelected.trip = evt.context.trip;
      paymentOptionSelected.experience = evt.context.experience;
      paymentOptionSelected.nrOfAdults = evt.context.nrOfAdults;
      paymentOptionSelected.nrOfChildren = evt.context.nrOfChildren;
      bus.emit(addDefaultParameters(paymentOptionSelected, evt));
    });
    ifMatch(
      evt,
      {
        name: 'view',
        context: { trip: true, component: 'confirmation-screen-success' },
      },
      () => {
        const bookingConfirmed = new BookingConfirmed();
        bookingConfirmed.trip = evt.context.trip;
        if (bookingConfirmed.trip?.id) {
          fireOnce(`bookingConfirmed.${bookingConfirmed.trip.id}`, () => {
            bus.emit(addDefaultParameters(bookingConfirmed, evt));
          });
        }
      },
    );
    ifMatch(
      evt,
      {
        name: 'view',
        context: { variant: true, section: 'payment-list' },
      },
      () => {
        const paymentFormType = new PaymentFormType();
        paymentFormType.variant = evt.context.variant;
        bus.emit(addDefaultParameters(paymentFormType, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'click',
        context: { component: 'ExperienceReview' },
      },
      () => {
        const experienceReviewUpvoteChange = new ExperienceReviewUpvoteChange();
        experienceReviewUpvoteChange.experience_id = evt.context?.experience_id;
        experienceReviewUpvoteChange.review_id = evt.context?.review_id;
        experienceReviewUpvoteChange.action = evt.context?.buttonText as
          | 'add_upvote'
          | 'remove_upvote';
        bus.emit(addDefaultParameters(experienceReviewUpvoteChange, evt));
      },
    );
    ifMatch(
      evt,
      {
        name: 'submit',
        context: { component: 'LocalPicker' },
      },
      () => {
        const localPickerSubmitted = new LocalPickerSubmitted();
        localPickerSubmitted.experience_id = evt.context?.experience_id;
        localPickerSubmitted.options = evt.context?.options;
        localPickerSubmitted.selected = evt.context?.selected;
        bus.emit(addDefaultParameters(localPickerSubmitted, evt));
      },
    );

    ifMatch(
      evt,
      {
        name: 'click',
      },
      () => {
        const buttonClicked = new Click();
        buttonClicked.experience_id = evt.context?.experience_id;
        buttonClicked.rating = evt.context?.rating;
        buttonClicked.city = evt.context?.city;
        buttonClicked.country = evt.context?.country;
        buttonClicked.buttonText = evt.context?.buttonText;
        buttonClicked.action = evt.context?.action;
        buttonClicked.date = evt.context?.date;
        buttonClicked.time = evt.context?.time;
        buttonClicked.host = evt.context?.host;
        buttonClicked.languages = evt.context?.languages;
        buttonClicked.specialties = evt.context?.specialties;
        buttonClicked.nrOfAdults = evt.context?.nrOfAdults;
        buttonClicked.nrOfChildren = evt.context?.nrOfChildren;
        buttonClicked.keywords = evt.context?.keywords;
        buttonClicked.subCategories = evt.context?.subCategories;
        buttonClicked.transportMode = evt.context?.transportMode;
        buttonClicked.price = evt.context?.price;
        buttonClicked.durationFilter = evt.context?.durationFilter;
        buttonClicked.familyFriendly = evt.context?.familyFriendly;
        buttonClicked.instantConfirmation = evt.context?.instantConfirmation;
        buttonClicked.date_from = evt.context?.date_from;
        buttonClicked.date_to = evt.context?.date_to;
        bus.emit(addDefaultParameters(buttonClicked, evt));
      },
    );
  };
};
